import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatVolume,
  muscleKey,
  weeklyTargetDegradedMax,
  weeklyTargetRowLabel,
  type CatalogMuscleSlug,
  type HomeWeekly,
  type UnitsPreference,
  type WeeklyTargetsData,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Button } from "../primitives/Button";
import { DisclosureCard } from "../primitives/DisclosureCard";
import { EmptyState } from "../primitives/EmptyState";
import { Eyebrow } from "../primitives/Eyebrow";
import { StatPair } from "../primitives/StatPair";
import { TargetMeterRow } from "../primitives/TargetMeterRow";
import { weightUnitLabels } from "../weightUnitLabels";
import { makeHomeBoxStyle } from "./glanceCardStyles";

// AP-3 — the Home three-zone weekly targets meter. A foldable box (collapsed by
// default) that scores the last 7 days of training against the athlete's own weekly
// targets, whatever program is pinned (#393). Fed by shared computeWeeklyTargets
// (the host runs the selector, this renders it).
//
// AP-1b-u (2026-07-07): the meter is now the ONE "this week" card — it folds the
// workout count + volume (was WeeklyStatsCard, retired; last 7 days, #463) into
// its collapsed header as a stat row, so the numbers glance without expanding.
// It also scores tonnage + exercise-axis targets, not just muscle hard-sets:
// each row carries its `metric`, so a hard-set row reads "12 / 15" (bare count)
// and a tonnage row reads "5,000 kg / 6,000 kg" (weight, unit-preference aware).
//
// Target-vs-actual grammar (the track's non-negotiable invariant): the ACTUAL
// is a solid zone-coloured fill; the TARGET is a *different mark* — a soft tint
// band (±band tolerance) plus a down-caret marker at the exact goal, never a
// second solid fill. Zone is reinforced by a glyph + word, never colour alone
// (a11y). `over` uses `warn` gold, never `danger` red. Each row is the shared
// <TargetMeterRow> (label + value pair + zone + <TargetBar>), lifted at AP-3b's
// 2nd callsite (the in-workout strip) so this card and the strip never diverge.

type Props = {
  data: WeeklyTargetsData | null;
  theme: UiTheme;
  units: UnitsPreference;
  locale: string;
  // Last-7-days headline numbers, folded into the collapsed header
  // (AP-1b-u — WeeklyStatsCard retired). Null → the header omits the stat row.
  weekly: HomeWeekly | null;
  // Resolve an exercise slug → display name, for exercise-axis rows. Muscle
  // rows resolve via muscleKey() here; exercise names come from the catalog.
  resolveExerciseName: (slug: string) => string;
  // Drill into Progress — restores the tap-to-Progress path the folded-in
  // WeeklyStatsCard used to own (shown as an explicit link in the open body).
  onOpenProgress: () => void;
  // ME-6 — has the athlete set any weekly targets? `false` (the doc is empty)
  // turns the card into the "set your goals" invite instead of the meter, ALWAYS
  // (Antenore 2026-07-16 — even with recent training; a goal-less athlete's next
  // step is setting goals, not glancing top-patterns). Undefined ⇒ unknown/loading
  // ⇒ keep the meter (never flash the empty-state before the fetch lands). Other
  // callsites (in-workout strip, web Home) don't pass it → meter as before.
  hasAthleteTargets?: boolean;
  // Host-injected CTA for the empty-state. Absent (e.g. before the Me/Obiettivi
  // screen lands in ME-1) ⇒ the invite shows without a button — never a broken
  // one.
  onSetGoals?: () => void;
};

export function WeeklyTargetsCard({
  data,
  theme,
  units,
  locale,
  weekly,
  resolveExerciseName,
  onOpenProgress,
  hasAthleteTargets,
  onSetGoals,
}: Props) {
  const { t } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);

  // ME-6 state 3 — the athlete has no targets doc yet: the card becomes the
  // invite to set goals, winning over both the scored meter and the degraded
  // "top patterns" (no goals ⇒ nothing to score, and the next step is setting
  // them, not glancing patterns). Only when the answer is a definite `false`;
  // undefined (still loading) keeps the meter so the empty-state never flashes.
  if (hasAthleteTargets === false) {
    return (
      <View style={styles.emptyBox}>
        <EmptyState
          theme={theme}
          icon="target"
          title={t("mobile.home.targetsEmptyTitle", {
            defaultValue: "No goals yet",
          })}
          sub={t("mobile.home.targetsEmptySub", {
            defaultValue:
              "Set how much you want to train each muscle per week. Your plan works toward it; the Home meter shows where you are.",
          })}
        />
        {onSetGoals ? (
          <Button
            theme={theme}
            variant="primary"
            label={t("mobile.home.targetsEmptyCta", {
              defaultValue: "Set your goals",
            })}
            onPress={onSetGoals}
            style={styles.emptyCta}
          />
        ) : null}
      </View>
    );
  }

  // The card is the one "this week" surface: show it whenever there's either a
  // meter to score or last-7-days numbers to glance.
  // #116: rows-less data (only uncatalogued sets) counts as no meter.
  if ((data === null || data.rows.length === 0) && weekly === null) return null;

  const scored = data?.mode === "scored";
  const hasRows = data != null && data.rows.length > 0;

  // With rows, the title says what is scored: the athlete's weekly targets, not
  // a program (#393), or "Top patterns" degraded. With no meter (only weekly
  // numbers), there's no title at all: the eyebrow "Last 7 days" stands alone
  // above them (Antenore, 2026-09-24; the window since #463).
  const title = !hasRows
    ? null
    : scored
      ? t("composer.targetsTitle", { defaultValue: "Weekly targets" })
      : t("mobile.home.targetsTopPatterns", { defaultValue: "Top patterns" });
  // #393: muscles trained in the window with no target, said next to "on target".
  const untargeted = scored ? (data?.untargetedCount ?? 0) : 0;

  const resolveMuscle = (slug: string): string =>
    t(muscleKey(slug as CatalogMuscleSlug));

  const degradedMax = data ? weeklyTargetDegradedMax(data.rows) : 1;

  // The screen reader hears everything the sighted glance shows: the folded-in
  // workouts/volume numbers AND the on-target count (the retired WeeklyStatsCard
  // spelled its numbers out too — don't regress that).
  const weeklyA11y = weekly
    ? t("mobile.home.weeklyA11y", {
        sessions: weekly.sessionsLast7d,
        volume: formatVolume(weekly.volumeKgLast7d, units, locale, unitLabels),
        defaultValue: "{{sessions}} workouts, {{volume}} volume",
      })
    : "";
  const targetA11y = scored
    ? t("mobile.home.targetsSummary", {
        on: data!.onTargetCount,
        total: data!.targetCount,
        defaultValue: "{{on}} of {{total}} on target",
      })
    : "";
  const untargetedA11y =
    untargeted > 0
      ? t("mobile.home.targetsUntargetedA11y", {
          count: untargeted,
          defaultValue: "{{count}} muscles trained without a target",
        })
      : "";
  const eyebrow = t("mobile.home.weekEyebrow", { defaultValue: "Last 7 days" });
  // The eyebrow names the card for the screen reader too: without rows it is
  // the only name the card has.
  const a11yLabel = [eyebrow, title, weeklyA11y, targetA11y, untargetedA11y]
    .filter(Boolean)
    .join(". ");

  return (
    <DisclosureCard
      theme={theme}
      disabled={!hasRows}
      accessibilityLabel={a11yLabel}
      boxStyle={styles.box}
      header={
        <>
          <Eyebrow theme={theme}>{eyebrow}</Eyebrow>
          {title ? (
            <Text style={styles.title} numberOfLines={1}>
              {title}
            </Text>
          ) : null}
          <View style={styles.statsRow}>
            {weekly ? (
              <>
                <StatPair
                  theme={theme}
                  value={String(weekly.sessionsLast7d)}
                  label={t("mobile.home.weeklySessions", { defaultValue: "Workouts" })}
                />
                <StatPair
                  theme={theme}
                  value={formatVolume(weekly.volumeKgLast7d, units, locale, unitLabels)}
                  label={t("mobile.home.weeklyVolume", { defaultValue: "Volume" })}
                />
              </>
            ) : null}
            {scored ? (
              <StatPair
                theme={theme}
                value={`${data!.onTargetCount}/${data!.targetCount}`}
                label={t("mobile.home.targetsZoneOnTarget", { defaultValue: "on target" })}
              />
            ) : null}
            {untargeted > 0 ? (
              <StatPair
                theme={theme}
                value={String(untargeted)}
                label={t("mobile.home.targetsUntargeted", { defaultValue: "no target" })}
              />
            ) : null}
          </View>
        </>
      }
    >
      {hasRows ? (
        <>
          {data!.rows.map((row) => (
            <View key={`${row.axis}:${row.key}:${row.metric}`} style={styles.row}>
              <TargetMeterRow
                theme={theme}
                row={row}
                label={weeklyTargetRowLabel(row, resolveExerciseName, resolveMuscle)}
                units={units}
                locale={locale}
                degradedMax={degradedMax}
              />
            </View>
          ))}

          {scored ? null : (
            <Text style={styles.note}>
              {t("mobile.home.targetsHint", {
                defaultValue:
                  "Set your weekly goals in Me → Goals to score these against them.",
              })}
            </Text>
          )}

          {/* Drill-in the folded-in WeeklyStatsCard used to own on its numbers. */}
          <Pressable
            onPress={onOpenProgress}
            accessibilityRole="link"
            accessibilityLabel={t("mobile.home.viewProgress", {
              defaultValue: "View progress",
            })}
            style={({ pressed }) => [styles.progressLink, pressed && styles.pressed]}
          >
            <Text style={styles.progressText}>
              {t("mobile.home.viewProgress", { defaultValue: "View progress" })}
            </Text>
            <Text style={styles.progressText}>›</Text>
          </Pressable>
        </>
      ) : null}
    </DisclosureCard>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    // The Home margin box (makeHomeBoxStyle) — passed to <DisclosureCard> as its
    // boxStyle so this card keeps its Home gutters; the primitive supplies the
    // border/radius/surface + the header/chevron/divided-body chrome.
    box: {
      ...makeHomeBoxStyle(theme),
      backgroundColor: p.surface,
    },
    // ME-6 state-3 box: the standard Home box (same as `newsBox` in HomeView),
    // no extra padding — the <EmptyState> owns its own vertical/horizontal
    // inset, so the invite reads with the identical rhythm as every other Home
    // empty-state. Just a plain View (no disclosure chrome — nothing to expand).
    emptyBox: {
      ...makeHomeBoxStyle(theme),
      backgroundColor: p.surface,
    },
    // The CTA sits centred below the EmptyState (whose own paddingBottom gives
    // the gap above it); marginBottom keeps it off the card's lower edge.
    emptyCta: { alignSelf: "center", marginBottom: s.s5 },
    pressed: { opacity: 0.7 },
    title: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "600",
    },
    // The folded last-7-days numbers + on-target count, side by side.
    statsRow: { flexDirection: "row", flexWrap: "wrap", gap: s.s6, marginTop: s.s2 },
    // Each row = a bordered wrapper around the shared <TargetMeterRow>.
    row: {
      paddingTop: s.s3,
      paddingBottom: s.s2,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
    },
    note: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      paddingTop: s.s3,
      lineHeight: ty.sizes.sm * 1.4,
    },
    progressLink: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: s.s2,
      minHeight: 44,
    },
    progressText: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
